import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Radio,
  RadioGroup,
  Rating,
  Select,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import SportsEsportsOutlinedIcon from '@mui/icons-material/SportsEsportsOutlined';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import UploadIcon from '@mui/icons-material/Upload';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SendIcon from '@mui/icons-material/Send';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import { fetchAuthSession, fetchUserAttributes } from 'aws-amplify/auth';
import { Amplify } from 'aws-amplify';
import awsconfig from '../../aws-exports';
import api_service from '../../network/api_callbacks';

Amplify.configure(awsconfig);

const DEFAULT_THEME = {
  headerTitle: 'SHARE YOUR',
  headerHighlight: 'EXPERIENCE',
  subtitle: 'Help us make Rapchik better!',
  footerText: 'Your feedback can help shape a better Rapchik!',
  submitButtonText: 'SUBMIT FEEDBACK',
  showRewardsBanner: false,
  rewardsBannerText: 'Best ideas can win Rewards, Awards & Recognition Certificate!',
  showNumberedBadges: false,
  showBackButton: true,
  brandName: 'Rapchik',
};

const MAX_FILE_BYTES = 2.5 * 1024 * 1024;

const ICON_MAP = {
  person: PersonOutlineIcon,
  chat: ChatBubbleOutlineIcon,
  chart: TrendingUpIcon,
  bulb: LightbulbOutlinedIcon,
  gamepad: SportsEsportsOutlinedIcon,
  phone: PhoneIphoneIcon,
  email: EmailOutlinedIcon,
  whatsapp: ChatBubbleOutlineIcon,
  camera: PhotoCameraOutlinedIcon,
  comment: ChatBubbleOutlineIcon,
  star: StarOutlineIcon,
};

const emptyAnswers = (fields = []) => {
  const initial = {};
  fields.forEach((field) => {
    if (field.type === 'checkbox') initial[field.id] = [];
    else if (field.type === 'file') initial[field.id] = null;
    else if (field.type === 'rating' || field.type === 'number') initial[field.id] = '';
    else initial[field.id] = '';
  });
  return initial;
};

const highlightBrand = (text = '', brand = 'Rapchik') => {
  if (!text) return null;
  const parts = String(text).split(new RegExp(`(${brand})`, 'gi'));
  return parts.map((part, index) =>
    part.toLowerCase() === brand.toLowerCase() ? (
      <Box component="span" key={index} sx={{ color: '#FFE566', fontWeight: 700 }}>
        {part}
      </Box>
    ) : (
      <React.Fragment key={index}>{part}</React.Fragment>
    )
  );
};

function FieldIcon({ icon, color, badgeNumber, showBadge }) {
  const IconCmp = ICON_MAP[icon] || ChatBubbleOutlineIcon;
  return (
    <Box sx={{ position: 'relative', flexShrink: 0, mt: 0.25 }}>
      <Box
        sx={{
          width: 42,
          height: 42,
          borderRadius: '10px',
          bgcolor: color || '#7B08EF',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 10px rgba(0,0,0,0.12)',
          color: '#fff',
        }}
      >
        {icon === 'whatsapp' ? (
          <Typography fontWeight={800} fontSize={18} lineHeight={1}>
            W
          </Typography>
        ) : (
          <IconCmp sx={{ fontSize: 22 }} />
        )}
      </Box>
      {showBadge && badgeNumber ? (
        <Box
          sx={{
            position: 'absolute',
            top: -6,
            right: -6,
            width: 18,
            height: 18,
            borderRadius: '50%',
            bgcolor: color || '#7B08EF',
            color: '#fff',
            fontSize: 11,
            fontWeight: 800,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '2px solid #fff',
          }}
        >
          {badgeNumber}
        </Box>
      ) : null}
    </Box>
  );
}

function FieldShell({ field, theme, error, children }) {
  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1.25,
        alignItems: 'flex-start',
        width: field.width === 'half' ? { xs: '100%', sm: 'calc(50% - 8px)' } : '100%',
        mb: 2,
      }}
    >
      <FieldIcon
        icon={field.icon}
        color={field.iconColor}
        badgeNumber={field.badgeNumber}
        showBadge={theme.showNumberedBadges}
      />
      <Box flex={1} minWidth={0}>
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 700,
            color: '#2d2145',
            mb: 0.75,
            lineHeight: 1.35,
            textAlign: 'left',
          }}
        >
          {field.label}
          {field.required ? (
            <Box component="span" sx={{ color: '#E53935', ml: 0.35 }}>
              *
            </Box>
          ) : null}
        </Typography>
        {children}
        {error ? (
          <Typography sx={{ color: '#E53935', fontSize: 12, mt: 0.5, textAlign: 'left' }}>
            {error}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}

const inputSx = {
  width: '100%',
  border: 'none',
  outline: 'none',
  bgcolor: '#F3F1F6',
  borderRadius: '12px',
  px: 1.5,
  py: 1.35,
  fontSize: 14,
  fontFamily: 'inherit',
  color: '#2d2145',
  resize: 'vertical',
  boxSizing: 'border-box',
  '&::placeholder': { color: '#9a90ab' },
};

function TextInput({
  multiline,
  value,
  onChange,
  placeholder,
  type = 'text',
  maxLength,
  inputMode,
  pattern,
  endAdornment,
}) {
  const Comp = multiline ? 'textarea' : 'input';
  const length = String(value || '').length;
  const showCounter = Boolean(maxLength) && maxLength > 0;

  return (
    <Box sx={{ position: 'relative' }}>
      <Box
        component={Comp}
        type={multiline ? undefined : type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        rows={multiline ? 3 : undefined}
        maxLength={maxLength || undefined}
        inputMode={inputMode}
        pattern={pattern}
        sx={{
          ...inputSx,
          pr: endAdornment ? 4.5 : 1.5,
          minHeight: multiline ? 84 : undefined,
          pb: showCounter ? 3.25 : undefined,
        }}
      />
      {endAdornment ? (
        <Box
          sx={{
            position: 'absolute',
            right: 12,
            top: multiline ? 12 : '50%',
            transform: multiline ? 'none' : 'translateY(-50%)',
            color: '#7B08EF',
            display: 'flex',
            pointerEvents: 'none',
          }}
        >
          {endAdornment}
        </Box>
      ) : null}
      {showCounter ? (
        <Typography
          sx={{
            position: 'absolute',
            right: 12,
            bottom: 8,
            fontSize: 11,
            color: length >= maxLength ? '#E53935' : '#9a90ab',
            pointerEvents: 'none',
          }}
        >
          {length}/{maxLength}
        </Typography>
      ) : null}
    </Box>
  );
}

function groupFields(fields) {
  const rows = [];
  let i = 0;
  while (i < fields.length) {
    const current = fields[i];
    const next = fields[i + 1];
    if (current.width === 'half' && next?.width === 'half') {
      rows.push([current, next]);
      i += 2;
    } else {
      rows.push([current]);
      i += 1;
    }
  }
  return rows;
}

export default function FeedbackFormPublic() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  // Form-only share token (?token=). Must NOT be written to dashboard auth storage.
  const urlToken = (searchParams.get('token') || '').trim() || null;
  const fileRefs = useRef({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [answers, setAnswers] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [guestEmail, setGuestEmail] = useState('');
  const [guestName, setGuestName] = useState('');
  const [authUser, setAuthUser] = useState(null);
  const [idToken, setIdToken] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const applyUserProfile = (user) => {
      if (!user) return;
      setAuthUser({
        email: user.email || '',
        name: user.name || '',
        userId: user.userId || '',
      });
      if (user.email) setGuestEmail(user.email);
      if (user.name) setGuestName(user.name);
    };

    const bootstrap = async () => {
      setLoading(true);
      setError('');
      try {
        let formToken = urlToken;

        // Prefer explicit form share token from URL (form-only access, no dashboard session)
        if (urlToken) {
          setIdToken(urlToken);
        } else {
          // Optional: Cognito browser session on same origin (still PublicApp only — no dashboard shell)
          try {
            const session = await fetchAuthSession();
            const token =
              session.tokens?.idToken?.toString() || session.tokens?.accessToken?.toString();
            if (token) {
              formToken = token;
              setIdToken(token);
              try {
                const attrs = await fetchUserAttributes();
                if (!cancelled) {
                  applyUserProfile({
                    email: attrs.email || '',
                    name: attrs.name || attrs.given_name || attrs.preferred_username || '',
                    userId: attrs.sub || '',
                  });
                }
              } catch {
                // guest path
              }
            }
          } catch {
            // no Cognito session
          }
        }

        const response = await api_service.getPublicFeedbackForm(slug, formToken);
        if (cancelled) return;

        if (response?.responseCode === 200 && response?.data) {
          setForm(response.data);
          setAnswers(emptyAnswers(response.data.fields || []));

          // Attach verified profile from API when ?token= was provided
          if (response.data.user?.userId || response.data.user?.email) {
            applyUserProfile(response.data.user);
          } else if (response.data.tokenError) {
            setError(response.data.tokenError);
          }
        } else {
          const message =
            response?.error?.message ||
            response?.response?.data?.error?.message ||
            'Unable to load this form';
          setError(message);
          setForm(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.data?.error?.message || err?.message || 'Unable to load this form');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    bootstrap();
    return () => {
      cancelled = true;
    };
  }, [slug, urlToken]);

  const theme = useMemo(() => ({ ...DEFAULT_THEME, ...(form?.theme || {}) }), [form]);

  const sortedFields = useMemo(
    () => (form?.fields || []).slice().sort((a, b) => a.order - b.order),
    [form]
  );

  const fieldRows = useMemo(() => groupFields(sortedFields), [sortedFields]);

  const hasEmailField = useMemo(
    () => sortedFields.some((field) => field.type === 'email'),
    [sortedFields]
  );

  const hasNameField = useMemo(
    () =>
      sortedFields.some((field) =>
        /name|username/i.test(field.label || '')
      ),
    [sortedFields]
  );

  const setAnswer = (fieldId, value) => {
    setAnswers((prev) => ({ ...prev, [fieldId]: value }));
    setFieldErrors((prev) => {
      if (!prev[fieldId]) return prev;
      const next = { ...prev };
      delete next[fieldId];
      return next;
    });
  };

  const readFileAsDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleFilePick = async (fieldId, file) => {
    if (!file) {
      setAnswer(fieldId, null);
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      setFieldErrors((prev) => ({
        ...prev,
        [fieldId]: 'File must be under 2.5 MB',
      }));
      return;
    }
    try {
      const dataUrl = await readFileAsDataUrl(file);
      setAnswer(fieldId, {
        fileName: file.name,
        fileType: file.type,
        size: file.size,
        dataUrl,
      });
    } catch {
      setFieldErrors((prev) => ({ ...prev, [fieldId]: 'Could not read file' }));
    }
  };

  const validateClient = () => {
    const nextErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!hasEmailField) {
      if (!authUser?.email && !guestEmail.trim()) {
        nextErrors.__email = 'Email is required';
      } else if (!authUser?.email && guestEmail && !emailRegex.test(guestEmail.trim())) {
        nextErrors.__email = 'Enter a valid email (e.g. name@gmail.com)';
      }
    }

    if (!authUser?.name && guestName && guestName.length > 50) {
      nextErrors.__name = 'Maximum 50 characters';
    }

    sortedFields.forEach((field) => {
      const value = answers[field.id];
      const empty =
        value === undefined ||
        value === null ||
        value === '' ||
        (Array.isArray(value) && value.length === 0) ||
        (field.type === 'file' && !(value?.fileName || value?.dataUrl));

      if (field.required && empty) {
        nextErrors[field.id] = 'This field is required';
        return;
      }
      if (empty) return;

      if (field.type === 'email' && !emailRegex.test(String(value).trim())) {
        nextErrors[field.id] = 'Enter a valid email (e.g. name@gmail.com)';
      }

      if (field.type === 'phone') {
        const limit = field.maxLength && field.maxLength > 0 ? Number(field.maxLength) : 10;
        if (!new RegExp(`^\\d{${limit}}$`).test(String(value))) {
          nextErrors[field.id] = `Enter exactly ${limit} digits (numbers only, no spaces)`;
        }
      }

      if (
        (field.type === 'text' || field.type === 'textarea') &&
        field.maxLength &&
        String(value).length > Number(field.maxLength)
      ) {
        nextErrors[field.id] = `Maximum ${field.maxLength} characters`;
      }

      if (field.type === 'number' && Number.isNaN(Number(value))) {
        nextErrors[field.id] = 'Enter a valid number';
      }
      if (field.type === 'rating') {
        const num = Number(value);
        if (Number.isNaN(num) || num < 1 || num > 5) {
          nextErrors[field.id] = 'Select a rating from 1 to 5';
        }
      }
    });

    setFieldErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const resolveSubmitEmail = () => {
    if (authUser?.email) return authUser.email;
    if (guestEmail.trim()) return guestEmail.trim();
    const emailField = sortedFields.find((field) => field.type === 'email');
    if (emailField) return String(answers[emailField.id] || '').trim();
    return '';
  };

  const resolveSubmitName = () => {
    if (authUser?.name) return authUser.name;
    if (guestName.trim()) return guestName.trim();
    const nameField = sortedFields.find((field) => /name|username/i.test(field.label || ''));
    if (nameField) return String(answers[nameField.id] || '').trim();
    return '';
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateClient()) return;

    setSubmitting(true);
    setError('');
    try {
      const payload = {
        email: resolveSubmitEmail(),
        name: resolveSubmitName() || undefined,
        answers: sortedFields.map((field) => {
          let value = answers[field.id];
          if (field.type === 'number' || field.type === 'rating') {
            value = value === '' || value === null || value === undefined ? value : Number(value);
          }
          if (field.type === 'file' && !value) value = '';
          return { fieldId: field.id, value };
        }),
      };

      const response = await api_service.submitPublicFeedback(slug, payload, idToken);
      if (response?.responseCode === 201 || response?.responseCode === 200) {
        setSuccess(true);
      } else {
        setError(
          response?.error?.message ||
            response?.response?.data?.error?.message ||
            'Submission failed. Please try again.'
        );
      }
    } catch (err) {
      setError(err?.response?.data?.error?.message || err?.message || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  const renderFieldControl = (field) => {
    const value = answers[field.id];
    const helper = fieldErrors[field.id];

    if (field.type === 'file') {
      const fileMeta = value;
      return (
        <FieldShell key={field.id} field={field} theme={theme} error={helper}>
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap">
            <Typography sx={{ color: '#8a7fa0', fontSize: 13 }}>
              {fileMeta?.fileName || field.placeholder || 'Upload image or video'}
            </Typography>
            <Box
              component="button"
              type="button"
              onClick={() => fileRefs.current[field.id]?.click()}
              sx={{
                border: '1.5px solid #7B08EF',
                bgcolor: '#fff',
                color: '#7B08EF',
                borderRadius: '10px',
                px: 1.75,
                py: 0.75,
                fontWeight: 700,
                fontSize: 13,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                fontFamily: 'inherit',
              }}
            >
              <UploadIcon sx={{ fontSize: 16 }} />
              Upload
            </Box>
            <input
              ref={(el) => {
                fileRefs.current[field.id] = el;
              }}
              type="file"
              accept="image/*,video/*"
              hidden
              onChange={(e) => handleFilePick(field.id, e.target.files?.[0])}
            />
          </Box>
        </FieldShell>
      );
    }

    if (field.type === 'select') {
      return (
        <FieldShell key={field.id} field={field} theme={theme} error={helper}>
          <FormControl fullWidth size="small">
            <Select
              value={value || ''}
              displayEmpty
              onChange={(e) => setAnswer(field.id, e.target.value)}
              IconComponent={KeyboardArrowDownIcon}
              sx={{
                bgcolor: '#F3F1F6',
                borderRadius: '12px',
                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                '& .MuiSelect-select': { py: 1.35, fontSize: 14 },
              }}
            >
              <MenuItem value="" disabled>
                {field.placeholder || 'Select'}
              </MenuItem>
              {(field.options || []).map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </FieldShell>
      );
    }

    if (field.type === 'radio') {
      return (
        <FieldShell key={field.id} field={field} theme={theme} error={helper}>
          <RadioGroup value={value} onChange={(e) => setAnswer(field.id, e.target.value)}>
            {(field.options || []).map((option) => (
              <FormControlLabel key={option} value={option} control={<Radio size="small" />} label={option} />
            ))}
          </RadioGroup>
        </FieldShell>
      );
    }

    if (field.type === 'checkbox') {
      const selected = Array.isArray(value) ? value : [];
      return (
        <FieldShell key={field.id} field={field} theme={theme} error={helper}>
          <FormGroup>
            {(field.options || []).map((option) => (
              <FormControlLabel
                key={option}
                control={
                  <Checkbox
                    size="small"
                    checked={selected.includes(option)}
                    onChange={(e) => {
                      if (e.target.checked) setAnswer(field.id, [...selected, option]);
                      else setAnswer(field.id, selected.filter((item) => item !== option));
                    }}
                  />
                }
                label={option}
              />
            ))}
          </FormGroup>
        </FieldShell>
      );
    }

    if (field.type === 'rating') {
      return (
        <FieldShell key={field.id} field={field} theme={theme} error={helper}>
          <Rating
            value={value === '' ? null : Number(value)}
            onChange={(_, next) => setAnswer(field.id, next || '')}
          />
        </FieldShell>
      );
    }

    return (
      <FieldShell key={field.id} field={field} theme={theme} error={helper}>
        <TextInput
          multiline={field.type === 'textarea'}
          type={
            field.type === 'number'
              ? 'number'
              : field.type === 'email'
                ? 'email'
                : field.type === 'phone'
                  ? 'tel'
                  : 'text'
          }
          inputMode={field.type === 'phone' ? 'numeric' : undefined}
          pattern={field.type === 'phone' ? '[0-9]*' : undefined}
          maxLength={
            field.type === 'phone'
              ? field.maxLength && field.maxLength > 0
                ? Number(field.maxLength)
                : 10
              : field.maxLength && field.maxLength > 0
                ? Number(field.maxLength)
                : undefined
          }
          value={value || ''}
          onChange={(e) => {
            let next = e.target.value;
            if (field.type === 'phone') {
              const limit = field.maxLength && field.maxLength > 0 ? Number(field.maxLength) : 10;
              next = next.replace(/\D/g, '').slice(0, limit);
            } else if (
              (field.type === 'text' || field.type === 'textarea') &&
              field.maxLength &&
              field.maxLength > 0
            ) {
              next = next.slice(0, Number(field.maxLength));
            }
            setAnswer(field.id, next);
          }}
          placeholder={field.placeholder || ''}
          endAdornment={<EditOutlinedIcon sx={{ fontSize: 18 }} />}
        />
      </FieldShell>
    );
  };

  if (loading) {
    return (
      <Box
        minHeight="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        sx={{ background: 'linear-gradient(180deg, #FF4FA3 0%, #6B1AE8 55%, #4A0FB8 100%)' }}
      >
        <CircularProgress sx={{ color: '#fff' }} />
      </Box>
    );
  }

  if (success) {
    return (
      <Box
        minHeight="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        p={3}
        sx={{ background: 'linear-gradient(180deg, #FF4FA3 0%, #6B1AE8 55%, #4A0FB8 100%)' }}
      >
        <Box
          maxWidth={420}
          width="100%"
          bgcolor="#fff"
          borderRadius="28px"
          p={4}
          textAlign="center"
          boxShadow="0 20px 50px rgba(0,0,0,0.25)"
        >
          <EmojiEventsIcon sx={{ fontSize: 48, color: '#FFC107', mb: 1 }} />
          <Typography variant="h5" fontWeight={800} gutterBottom>
            Thank you!
          </Typography>
          <Typography color="text.secondary">
            Your feedback helps us build a better {theme.brandName}.
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error && !form) {
    return (
      <Box
        minHeight="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        p={3}
        sx={{ background: 'linear-gradient(180deg, #FF4FA3 0%, #6B1AE8 100%)' }}
      >
        <Alert severity="error" sx={{ maxWidth: 480, width: '100%', textAlign: 'left' }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      minHeight="100vh"
      sx={{
        background: `
          radial-gradient(circle at 15% 12%, rgba(255,255,255,0.18) 0%, transparent 28%),
          radial-gradient(circle at 85% 18%, rgba(255,229,102,0.2) 0%, transparent 22%),
          linear-gradient(180deg, #FF5AAF 0%, #9B2DE8 42%, #5A12D4 100%)
        `,
        py: { xs: 2, sm: 4 },
        px: 2,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          opacity: 0.35,
          backgroundImage: `
            radial-gradient(circle at 20% 30%, rgba(255,255,255,0.35) 0 2px, transparent 3px),
            radial-gradient(circle at 70% 20%, rgba(255,229,102,0.55) 0 2px, transparent 3px),
            radial-gradient(circle at 40% 70%, rgba(255,255,255,0.25) 0 1.5px, transparent 2.5px),
            radial-gradient(circle at 80% 75%, rgba(255,255,255,0.3) 0 2px, transparent 3px)
          `,
        }}
      />

      <Box
        component="form"
        onSubmit={handleSubmit}
        maxWidth={520}
        mx="auto"
        position="relative"
        zIndex={1}
      >
        <Box display="flex" alignItems="flex-start" gap={1.5} mb={2.5} pt={1}>
          {theme.showBackButton ? (
            <Box
              component="button"
              type="button"
              onClick={() => window.history.length > 1 && window.history.back()}
              aria-label="Back"
              sx={{
                width: 40,
                height: 40,
                borderRadius: '12px',
                border: 'none',
                bgcolor: 'rgba(60, 10, 120, 0.85)',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                flexShrink: 0,
                mt: 0.5,
              }}
            >
              <ArrowBackIcon fontSize="small" />
            </Box>
          ) : null}

          <Box flex={1} textAlign="left" pt={0.5}>
            <Typography
              sx={{
                color: '#fff',
                fontWeight: 900,
                fontSize: { xs: 26, sm: 32 },
                letterSpacing: 0.5,
                lineHeight: 1.05,
                textTransform: 'uppercase',
              }}
            >
              {theme.headerTitle}{' '}
              <Box component="span" sx={{ color: '#FFE566' }}>
                {theme.headerHighlight}
              </Box>
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.92)', mt: 0.75, fontSize: 14 }}>
              {highlightBrand(theme.subtitle, theme.brandName)}
            </Typography>
          </Box>

          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              bgcolor: '#FFE566',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
              position: 'relative',
              fontSize: 36,
            }}
            aria-hidden
          >
            😎
            <Box
              sx={{
                position: 'absolute',
                top: -4,
                right: -4,
                width: 22,
                height: 22,
                borderRadius: '50%',
                bgcolor: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 12,
              }}
            >
              💜
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            bgcolor: '#fff',
            borderRadius: '28px',
            boxShadow: '0 18px 40px rgba(40, 0, 90, 0.28)',
            p: { xs: 2.25, sm: 3 },
            overflow: 'hidden',
          }}
        >
          {error ? (
            <Alert severity="error" sx={{ mb: 2, textAlign: 'left' }}>
              {error}
            </Alert>
          ) : null}

          {authUser?.email || authUser?.userId ? (
            <Alert severity="info" sx={{ mb: 2, textAlign: 'left' }}>
              Signed in as form user
              {authUser?.name ? ` · ${authUser.name}` : ''}
              {authUser?.email ? ` · ${authUser.email}` : ''}
              {urlToken ? ' (token link — form access only)' : ''}
            </Alert>
          ) : null}

          {!authUser?.email && !hasEmailField ? (
            <FieldShell
              field={{
                id: '__email',
                label: 'Email',
                required: true,
                icon: 'email',
                iconColor: '#FFC107',
                width: 'full',
              }}
              theme={theme}
              error={fieldErrors.__email}
            >
              <TextInput
                type="email"
                value={guestEmail}
                onChange={(e) => setGuestEmail(e.target.value)}
                placeholder="Enter your email (e.g. name@gmail.com)"
                endAdornment={<EditOutlinedIcon sx={{ fontSize: 18 }} />}
              />
            </FieldShell>
          ) : null}

          {!authUser?.name && !hasNameField && !hasEmailField ? (
            <FieldShell
              field={{
                id: '__name',
                label: 'Name / Username',
                required: false,
                icon: 'person',
                iconColor: '#7B08EF',
                width: 'full',
              }}
              theme={theme}
              error={fieldErrors.__name}
            >
              <TextInput
                value={guestName}
                maxLength={50}
                onChange={(e) => setGuestName(e.target.value.slice(0, 50))}
                placeholder="Enter your username"
                endAdornment={<EditOutlinedIcon sx={{ fontSize: 18 }} />}
              />
            </FieldShell>
          ) : null}

          {authUser?.email ? (
            <Alert severity="info" sx={{ mb: 2, textAlign: 'left' }}>
              Submitting as {authUser.email}
              {authUser.name ? ` (${authUser.name})` : ''}
            </Alert>
          ) : null}

          {fieldRows.map((row) => (
            <Box
              key={row.map((f) => f.id).join('-')}
              display="flex"
              flexWrap="wrap"
              gap={2}
              justifyContent="space-between"
            >
              {row.map((field) => renderFieldControl(field))}
            </Box>
          ))}

          {theme.showRewardsBanner ? (
            <Box
              sx={{
                mt: 1,
                mx: { xs: -2.25, sm: -3 },
                mb: { xs: -2.25, sm: -3 },
                bgcolor: '#6B1AE8',
                backgroundImage:
                  'radial-gradient(circle at 80% 40%, rgba(255,255,255,0.15) 0 2px, transparent 3px), radial-gradient(circle at 20% 70%, rgba(255,229,102,0.25) 0 2px, transparent 3px)',
                px: 2.5,
                py: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              <CardGiftcardIcon sx={{ color: '#FFE566', fontSize: 36, flexShrink: 0 }} />
              <Typography
                sx={{
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: 14,
                  lineHeight: 1.35,
                  textAlign: 'left',
                }}
              >
                {theme.rewardsBannerText}
              </Typography>
            </Box>
          ) : null}
        </Box>

        <Box
          component="button"
          type="submit"
          disabled={submitting}
          sx={{
            mt: 2.5,
            width: '100%',
            border: 'none',
            borderRadius: '999px',
            py: 1.6,
            px: 2,
            cursor: submitting ? 'wait' : 'pointer',
            background: 'linear-gradient(90deg, #FF4FA3 0%, #E91E63 55%, #D81B60 100%)',
            boxShadow: '0 10px 24px rgba(216, 27, 96, 0.45)',
            color: '#fff',
            fontWeight: 900,
            fontSize: 15,
            letterSpacing: 0.8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.25,
            fontFamily: 'inherit',
            opacity: submitting ? 0.75 : 1,
          }}
        >
          <EmojiEventsIcon sx={{ color: '#FFE566' }} />
          {submitting ? 'SUBMITTING…' : theme.submitButtonText || 'SUBMIT FEEDBACK'}
          <SendIcon sx={{ fontSize: 18 }} />
        </Box>

        <Typography
          sx={{
            mt: 2,
            mb: 2,
            color: 'rgba(255,255,255,0.95)',
            textAlign: 'center',
            fontSize: 14,
          }}
        >
          {highlightBrand(theme.footerText, theme.brandName)} ❤️
        </Typography>
      </Box>
    </Box>
  );
}
